import { Search } from "react-feather";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button } from "@shopify/polaris";
import DataTable from "react-data-table-component";
import Spinner from "../components/Spinner";
import "./Directory.css";
import "../components/DataTable.css";
import { useActions, useAtom } from "tiny-atom/react/hooks";
import { TextField } from "@shopify/polaris";
import moment from "moment";
import envelopeImage from "../assets/envelope-icon.png";
import { AppContext } from "../context/AppContext";
import BatchMessageModal from "../components/BatchMessageModal";
import useConversations from "../hooks/useConversations";
import Filters from "../components/assessments/filters/Filters";
import useArchivedFilter from "../components/directory/filters/ArchivedFilter";
import useGroupNameFilter from "../components/directory/filters/GroupNameFilter";
import { Flex } from "@core";

export default function Directory() {
  const { setPatient, setFilter, setCurrentScreen } = useActions();
  const { filter } = useAtom(state => ({ filter: state.filter }));
  const { loading, error } = useContext(AppContext);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showBatchMessageModal, setShowBatchMessageModal] = useState(false);
  const { refetchChildren, children, excludeArchived, setExcludeArchived } = useContext(AppContext);

  const {
    filterItem: groupNameFilter,
    selectedGroups,
    selectedCaregiverGroups
  } = useGroupNameFilter({
    children
  });

  const { conversations } = useConversations();

  const archivedFilter = useArchivedFilter({
    selected: excludeArchived,
    setSelected: setExcludeArchived
  });

  useEffect(() => {
    const currentTime = moment().valueOf();
    const lastRefetchTime = sessionStorage.getItem("lastChildRefetch");

    if (!lastRefetchTime) {
      sessionStorage.setItem("lastChildRefetch", currentTime);
    } else {
      const lastRefetchInMillis = parseInt(lastRefetchTime);

      if (Number.isInteger(lastRefetchInMillis)) {
        const nextRefetchTime = moment(lastRefetchInMillis).add(60, "seconds");
        if (moment().isAfter(nextRefetchTime)) {
          refetchChildren();
          sessionStorage.setItem("lastChildRefetch", currentTime);
        }
      }
    }
  }, [refetchChildren]);

  setCurrentScreen("therapistDirectoryScreen");

  function goToPatient(patient) {
    patient = children.find(p => p.user.id === patient.id).user;
    setPatient({ pathname: `/patient/${patient.id}`, params: { patient } });
  }

  const listParentNames = useCallback(user => {
    const parents = (user.parents || []).map(p => p.user);
    const includeLastNames = !user.lastName || parents.some(p => p.lastName && p.lastName !== user.lastName);

    return parents
      .map(p => {
        if (includeLastNames && p.lastName) {
          return `${p.firstName} ${p.lastName}`;
        }
        return p.firstName;
      })
      .join(", ");
  }, []);

  const getHasUnreadMesages = useCallback(
    client => {
      if (!conversations) {
        return false;
      }
      const familyMemberIds = [client.id, ...(client.parents || []).map(parent => parent.user.id)];

      return conversations
        .filter(
          conversation =>
            (conversation.familyId && conversation.familyId === client.familyId) ||
            conversation.users.some(u => familyMemberIds.includes(u.id))
        )
        .some(conversation => !conversation.read);
    },
    [conversations]
  );

  const formatChildren = useCallback(
    children => {
      let filteredData = [];
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        let user = child.user;
        filteredData.push({
          id: user.id,
          name: `${user.firstName} ${user.lastName ? user.lastName : ""}`,
          caregivers: listParentNames(user),
          lastActiveAt: user.lastActiveAt,
          unreadMessages: getHasUnreadMesages(user),
          createdAt: user.createdAt,
          familyId: user.familyId,
          groupName: user.groupName,
          ehrUserId: user.ehrUserId
        });
      }
      return filteredData;
    },
    [getHasUnreadMesages, listParentNames]
  );

  const filterChildren = useCallback(
    data => {
      const filteredChildren = [];

      for (const child of data) {
        let includeChild = true;

        if (filter) {
          const childName = `${child.user.firstName} ${child.user.lastName}`;
          const matchesNameFilter = childName.toLowerCase().includes(filter.toLowerCase());
          const matchesParentFilter = (child.user.parents || []).some(p =>
            `${p.user.firstName} ${p.user.lastName}`.toLowerCase().includes(filter.toLowerCase())
          );
          const matchedEhrId = child.user.ehrUserId && child.user.ehrUserId === filter.trim();
          const matchedCaregiverEhrId = (child.user.parents || []).some(
            p => p.user.ehrUserId && p.user.ehrUserId === filter.trim()
          );
          const matchedEmail =
            filter.includes("@") &&
            (child.user.parents || []).some(
              p => p.user.email && p.user.email.toLowerCase().trim().includes(filter.toLowerCase().trim())
            );

          if (!matchesNameFilter && !matchesParentFilter && !matchedEhrId && !matchedCaregiverEhrId && !matchedEmail) {
            includeChild = false;
          }
        }

        if (includeChild && (selectedCaregiverGroups.length > 0 || selectedGroups.length > 0)) {
          let matchesGroupFilter = child.user.groupName && selectedGroups.includes(child.user.groupName);
          let matchesCaregiverGroupFilter = child.user.parents?.some(
            parent => parent.user.groupName && selectedCaregiverGroups.includes(parent.user.groupName)
          );

          if (!(matchesGroupFilter || matchesCaregiverGroupFilter)) {
            includeChild = false;
          }
        }
        if (includeChild) {
          filteredChildren.push(child);
        }
      }
      return filteredChildren;
    },
    [filter, selectedGroups, selectedCaregiverGroups]
  );

  const UnreadMessagesImage = useCallback(
    ({ row }) => (
      <div
        style={{
          display: `${row.unreadMessages ? "flex" : "none"}`,
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "19px",
          backgroundColor: "#2F7AC8",
          width: "37px",
          height: "22px"
        }}
      >
        <img alt="unread messages" height="11px" width="14px" src={envelopeImage} style={{ color: "#ffffff" }} />
      </div>
    ),
    []
  );

  const changeFilter = useCallback(filter => {
    setFilter({ filter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          name: "CHILD",
          selector: row => row.name,
          sortable: true,
          width: "max(20%, 180px)"
        },
        {
          name: "CAREGIVERS",
          selector: row => row.caregivers,
          sortable: true,
          width: "max(27%, 200px)"
        },
        {
          name: "GROUP",
          wrap: true,
          style: { whiteSpace: "normal", wordWrap: "break-word" },
          selector: row => row.groupName,
          sortable: true,
          cell: row => {
            const value = row.groupName ? row.groupName : !row.ehrUserId ? "(Not Linked in Healthie)" : null;
            return (
              <div
                style={{
                  color: !row.groupName && !row.ehrUserId ? "#818589" : undefined
                }}
              >
                {value}
              </div>
            );
          },
          width: "20%"
        },
        {
          name: "LAST ACTIVE",
          selector: row => row.lastActiveAt,
          format: row => {
            const lastActiveAt = moment(row.lastActiveAt);
            if (lastActiveAt.isSame(moment(), "day")) {
              return "Today";
            } else if (lastActiveAt.isSame(moment().subtract(1, "day"), "day")) {
              return "Yesterday";
            } else if (!row.lastActiveAt) {
              return "";
            } else {
              return lastActiveAt.format("M/DD/YY");
            }
          },
          sortable: true,
          sortFunction: (rowA, rowB) => {
            if (!rowA.lastActiveAt && !rowB.lastActiveAt) {
              return moment(rowA.createdAt).diff(moment(rowB.createdAt));
            }
            return moment(rowA.lastActiveAt || 0).diff(moment(rowB.lastActiveAt || 0));
          },
          width: "max(150px, 20%)"
        },
        {
          name: "",
          selector: row => row.unreadMessages,
          cell: row => <UnreadMessagesImage row={row} />,
          width: "80px"
        }
      ].filter(Boolean),
    [UnreadMessagesImage]
  );

  const childrenData = useMemo(() => {
    return filter || selectedGroups.length > 0 || selectedCaregiverGroups.length > 0
      ? formatChildren(filterChildren(children))
      : formatChildren(children);
  }, [filter, selectedGroups.length, selectedCaregiverGroups.length, formatChildren, filterChildren, children]);

  if (loading) return <Spinner></Spinner>;
  if (error) return <p></p>;

  const handleChange = ({ selectedRows: rows }) => {
    setSelectedRows(rows);
  };

  return (
    <div className="directory-data-table">
      <div className="data-table-header">
        <div className="data-table-title">Clients</div>
        <Flex row left center style={{ paddingLeft: 15 }}>
          <Flex style={{ minWidth: "150px" }}>
            <TextField
              prefix={<Search style={{ marginTop: "8px", marginRight: "8px" }} size={16} />}
              placeholder="Search"
              value={filter}
              type="search"
              onChange={changeFilter}
              clearButton
              onClearButtonClick={() => {
                setFilter({ filter: "" });
              }}
            />
          </Flex>
          <Filters showIcon filters={[archivedFilter, groupNameFilter]} />
        </Flex>
      </div>
      <div className="data-table-actions">
        {selectedRows.length > 0 && (
          <div className="data-table-actions-content">
            <span className="selected-count">{selectedRows.length}</span>{" "}
            <span className="selected-label">Client{selectedRows.length > 1 ? "s" : ""} selected</span>
            <Button
              className="selected-button"
              variant="primary"
              disabled={false}
              onClick={() => setShowBatchMessageModal(true)}
            >
              message
            </Button>
          </div>
        )}
      </div>
      <div className="data-table-content">
        <DataTable
          columns={columns}
          data={childrenData}
          onRowClicked={goToPatient}
          direction="ltr"
          noHeader
          highlightOnHover
          selectableRows
          onSelectedRowsChange={handleChange}
          customStyles={{
            headRow: {
              style: {
                paddingLeft: "5px"
              }
            },
            rows: {
              style: {
                paddingLeft: "5px"
              }
            }
          }}
        />
      </div>
      {showBatchMessageModal && (
        <BatchMessageModal clients={selectedRows} onClose={() => setShowBatchMessageModal(false)} />
      )}
    </div>
  );
}
